import { Component, ElementRef, HostListener, Inject, OnDestroy } from '@angular/core';
import {
  DwActionEventInfo,
  DwActionPreference,
  DwButtonType,
  DwComponent,
  DwComponentType,
  DwDataSort,
  DwEventService,
  DwGridActionLocation,
  DwGridEvent,
  DwGridEventType,
  DwMetaDataActionExpressionService,
  DwMetaDataFilterTileState,
  DwMetaDataService,
  DwMetaDataServiceToken,
  DwRoutingService
} from '@devwareapps/devware-cap';
import { Subscription } from 'rxjs';

import { AppMetaData, AppMetaDataItemNames } from '../../../../meta-data/app-meta-data.service';
import { AdminManagerService } from '../../../admin/services/admin-manager.service';
import { DwAviatorGridRendererBaseComponent } from '../../../shared/components/grids/dw-aviator-grid-renderer-base.component';
import { QuizRepositoryService } from '../../services/quiz-repository.service';
import {
  buildQuizRowVm, filterAndSortQuizVms, QUIZ_SORTS, QUIZ_TYPES, QuizListFilter, QuizRow, QuizRowVm, stripHtml
} from './quiz-insights-view.util';

interface DetailVm {
  vm: QuizRowVm;
  questions: { key: number; questionId: number; name: string; text: string }[];
  sourceQuizzes: { key: number; quizId: number; name: string }[];
  loading: boolean;
  error: string | null;
}

/** Stock grid actions that the custom toolbar replaces with its own controls. */
const HIDDEN_TOOLBAR_ACTIONS = /quick.?search|filter.?toggle/i;

/** Row actions that move out of the detail header into the "More" menu; destructive ones are styled red. */
const OVERFLOW_ROW_ACTIONS = /edit|delete|remove/i;
const DANGER_ROW_ACTIONS = /delete|remove/i;

/** Master/detail view over the existing CAP Quiz grid and quiz/question entities. */
@DwComponent({
  key: 'quiz-insights-grid-renderer',
  name: 'Quiz Overview Grid Renderer',
  isGlobal: false,
  parentItemName: AppMetaDataItemNames.Quiz,
  componentType: DwComponentType.gridRenderer
})
@Component({
  selector: 'app-quiz-insights-grid-renderer',
  templateUrl: './quiz-insights-grid-renderer.component.html',
  styleUrls: ['./quiz-insights-grid-renderer.component.scss']
})
export class QuizInsightsGridRendererComponent extends DwAviatorGridRendererBaseComponent<QuizRow> implements OnDestroy {
  readonly quizTypes = [{ key: null, label: 'All quiz types' }, ...QUIZ_TYPES];
  readonly sortOptions = QUIZ_SORTS;
  readonly skeletonRows = [1, 2, 3, 4];
  readonly skeletonListRows = [1, 2, 3, 4, 5, 6, 7, 8];
  filter: QuizListFilter = { search: '', typeId: null, sortKey: 'name' };
  allVms: QuizRowVm[] = [];
  visibleVms: QuizRowVm[] = [];
  shownLabel = '';
  hasMoreQuizzes = false;
  selectedKey: number | null = null;
  detail: DetailVm | null = null;
  /** Phone drill-down: list → detail. Ignored by CSS on wide screens. */
  detailOpen = false;
  leftActions: DwActionPreference[] = [];
  rightActions: DwActionPreference[] = [];
  rowActions: DwActionPreference[] = [];
  overflowRowActions: DwActionPreference[] = [];
  moreOpen = false;
  private detailSubscription: Subscription | null = null;

  constructor(
    dwRoutingService: DwRoutingService,
    adminManagerService: AdminManagerService,
    @Inject(DwMetaDataServiceToken) dwMetaDataService: DwMetaDataService,
    dwEventService: DwEventService,
    private mdActionExpressionService: DwMetaDataActionExpressionService,
    private quizRepository: QuizRepositoryService,
    private host: ElementRef<HTMLElement>
  ) {
    super(dwRoutingService, adminManagerService, dwMetaDataService, dwEventService);
  }

  setupRenderer(): void {
    this.rebuildActions();
    this.setSorts(this.getEffectiveSorts());
  }

  protected onGridConfigUpdated(): void {
    this.rebuildActions();
  }

  protected filterTileStateChange(state: DwMetaDataFilterTileState): void { }

  // ponytail: filter loaded quizzes locally; use server filters if the catalog outgrows this view
  protected resolveInitialPageSize(): number {
    return 500;
  }

  protected getEffectiveSorts(): DwDataSort[] {
    return [{ fieldName: AppMetaData.Quiz.Attributes.QuizName.fieldName, ascending: true } as DwDataSort];
  }

  protected areItemsEqual(a: QuizRow, b: QuizRow): boolean {
    return a?.QuizId === b?.QuizId;
  }

  protected afterLoad(): void {
    this.hasMoreQuizzes = this.canGoToNextPage();
    this.allVms = (this.items || []).map(row => buildQuizRowVm(row));
    this.applyView();
  }

  protected onLoadError(): void {
    this.detailSubscription?.unsubscribe();
    this.hasMoreQuizzes = false;
    this.allVms = [];
    this.visibleVms = [];
    this.detail = null;
    this.setSelectedItems([]);
  }

  /** Text search and filters are client-side, so the base class filter pipeline is a no-op here. */
  customFiltersChanged(): void {
    this.applyView();
  }

  // ── Toolbar ─────────────────────────────────────────────────

  viewChanged(): void {
    this.applyView();
  }

  clearFilters(): void {
    this.filter = { ...this.filter, search: '', typeId: null };
    this.applyView();
  }

  onToolbarAction(actionEvent: DwActionEventInfo): void {
    this.fireGridAction(actionEvent, null);
  }

  // ── List ────────────────────────────────────────────────────

  select(vm: QuizRowVm, event?: Event): void {
    this.selectedKey = vm.key;
    this.setSelectedItems([vm.row]);
    this.onGridEvent({
      eventType: DwGridEventType.rowSelectionChange, eventData: this.selectedItems, dwBrowserEvent: null
    } as DwGridEvent);
    this.onGridEvent({
      eventType: DwGridEventType.gridRowClick, eventData: vm.row, browserEvent: event, dwBrowserEvent: null
    } as DwGridEvent);
    this.showDetail(vm);
    if (window.matchMedia('(max-width: 960px)').matches) {
      this.detailOpen = true;
      this.host.nativeElement.scrollIntoView({ block: 'start' });
    }
  }

  closeDetail(): void {
    this.detailOpen = false;
    // Return the phone user to the row they opened, not the top of a 180-item list.
    setTimeout(() => this.host.nativeElement.querySelector('.qi-item--selected')?.scrollIntoView({ block: 'center' }));
  }

  onRowDoubleClick(vm: QuizRowVm, event?: Event): void {
    this.onGridEvent({
      eventType: DwGridEventType.gridRowDoubleClick, eventData: vm.row, browserEvent: event, dwBrowserEvent: null
    } as DwGridEvent);
  }

  onDetailAction(actionEvent: DwActionEventInfo): void {
    this.moreOpen = false;
    if (!actionEvent?.actionPreference || !this.detail) {
      return;
    }
    this.onGridEvent({
      eventType: DwGridEventType.rowActionButton,
      eventData: this.detail.vm.row,
      actionPreference: actionEvent.actionPreference,
      browserEvent: null,
      dwBrowserEvent: actionEvent.dwBrowserEvent
    } as DwGridEvent);
  }

  toggleMore(event: Event): void {
    event.stopPropagation();
    this.moreOpen = !this.moreOpen;
  }

  @HostListener('document:click', ['$event'])
  @HostListener('document:keydown.escape', ['$event'])
  closeMore(event: Event): void {
    const more = this.host.nativeElement.querySelector('.qi-more');
    if (this.moreOpen && !(event.type === 'click' && more?.contains(event.target as Node))) {
      this.moreOpen = false;
    }
  }

  /** Source quiz links jump to that quiz, clearing filters if they would hide it. */
  selectQuizById(quizId: number): void {
    const vm = this.allVms.find(item => item.key === quizId);
    if (!vm) {
      return;
    }
    if (!this.visibleVms.includes(vm)) {
      this.clearFilters();
    }
    this.select(vm);
    // After change detection has moved the selected class onto the new row.
    setTimeout(() => this.host.nativeElement.querySelector('.qi-item--selected')?.scrollIntoView({ block: 'nearest' }));
  }

  openQuestion(questionId: number): void {
    this.dwRoutingService.navigateToUrl(`/lms-admin/quiz-management/question-bank/edit/${questionId}`, null);
  }

  trackByVm(index: number, vm: QuizRowVm): number {
    return vm.key || index;
  }

  trackByKey(index: number, item: { key: number }): number {
    return item.key;
  }

  ngOnDestroy(): void {
    this.detailSubscription?.unsubscribe();
    super.ngOnDestroy();
  }

  private applyView(): void {
    this.visibleVms = filterAndSortQuizVms(this.allVms, this.filter);
    this.shownLabel = `${this.visibleVms.length} ${this.visibleVms.length === 1 ? 'quiz' : 'quizzes'}`;
    this.hasFilters = !!this.filter.search || this.filter.typeId !== null;

    const current = this.visibleVms.find(vm => vm.key === this.selectedKey) || this.visibleVms[0] || null;
    if (!current) {
      this.detailSubscription?.unsubscribe();
      this.selectedKey = null;
      this.detail = null;
      this.setSelectedItems([]);
      this.rebuildActions();
      return;
    }
    if (current.key !== this.selectedKey || !this.detail || this.selectedItems[0] !== current.row) {
      this.selectedKey = current.key;
      this.showDetail(current);
    }
    this.setSelectedItems([current.row]);
  }

  private showDetail(vm: QuizRowVm): void {
    this.detailSubscription?.unsubscribe();
    this.setSelectedItems([vm.row]);
    this.rebuildActions();
    this.detail = { vm, questions: [], sourceQuizzes: [], loading: true, error: null };
    this.detailSubscription = this.quizRepository.getQuizOverview(vm.key).subscribe(
      quiz => {
        this.detail = {
          vm: buildQuizRowVm({ ...vm.row, ...quiz }),
          questions: (quiz?.QuizQuestionLink || []).filter(link => !link.IsDeleted).map(link => ({
            key: link.QuizQuestionLinkId,
            questionId: link.QuestionId,
            name: link.Question?.QuestionName || link.QuestionIdDisplay || `Question #${link.QuestionId}`,
            text: stripHtml(link.Question?.Question)
          })),
          sourceQuizzes: (quiz?.QuizReferenceQuizLink || []).map(link => ({
            key: link.QuizReferenceQuizLinkId,
            quizId: link.ReferenceQuizId,
            name: link.ReferenceQuizIdDisplay || `Quiz #${link.ReferenceQuizId}`
          })),
          loading: false,
          error: null
        };
      },
      () => {
        this.detail = { vm, questions: [], sourceQuizzes: [], loading: false, error: 'Unable to load quiz questions.' };
      });
  }

  private fireGridAction(actionEvent: DwActionEventInfo, rows: QuizRow[] | null): void {
    if (!actionEvent?.actionPreference) {
      return;
    }
    this.onGridEvent({
      eventType: DwGridEventType.gridActionButton,
      eventCommand: actionEvent.actionPreference.actionKey,
      actionPreference: actionEvent.actionPreference,
      eventData: rows,
      dwBrowserEvent: actionEvent.dwBrowserEvent
    } as DwGridEvent);
  }

  private rebuildActions(): void {
    const prefs = this.gridConfig?.actionPreferences || {};
    const context = (this.gridConfig as any)?.resolvedContext || null;
    const resolve = (location: string): DwActionPreference[] =>
      this.mdActionExpressionService.executeActionEnabledVisibleRules(prefs[location] || [], null, context).initialResults || [];

    const isReplaced = (a: DwActionPreference) => HIDDEN_TOOLBAR_ACTIONS.test(`${a.actionKey} ${a.actionDisplay}`);

    this.leftActions = resolve(DwGridActionLocation.actionBarLeft).filter(a => !isReplaced(a));
    this.rightActions = resolve(DwGridActionLocation.actionBarRight).filter(a => !isReplaced(a));
    const rowActions = this.mdActionExpressionService.executeActionEnabledVisibleRules(
      prefs[DwGridActionLocation.rowButton] || [], this.selectedItems[0] || null, context).initialResults || [];
    const label = (a: DwActionPreference) => `${a.actionKey} ${a.actionDisplay}`;
    const isOverflow = (a: DwActionPreference) => OVERFLOW_ROW_ACTIONS.test(label(a));
    const isDanger = (a: DwActionPreference) => DANGER_ROW_ACTIONS.test(label(a));
    this.rowActions = rowActions.filter(a => !isOverflow(a));
    // Menu items need a text label (the stock edit/delete defaults are icon-only); destructive items go last.
    this.overflowRowActions = rowActions.filter(isOverflow)
      .sort((a, b) => Number(isDanger(a)) - Number(isDanger(b)))
      .map(a => ({
        ...a,
        buttonConfig: {
          ...a.buttonConfig,
          useButtonDefaults: false,
          useParentDisplay: true,
          buttonType: DwButtonType.standard,
          buttonTitle: '',
          buttonClass: isDanger(a) ? 'qi-menu-item qi-menu-item--danger' : 'qi-menu-item',
          iconClass: a.buttonConfig?.iconClass || (isDanger(a) ? 'dw-icon-delete-grid' : 'dw-icon-edit-grid')
        }
      }));
  }
}
